import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Wand2, Loader2, Check, X, ChevronRight, ChevronDown, Search,
  Pencil, Merge, FolderTree, AlertTriangle, CheckSquare, Square,
  ArrowRight, RefreshCw, Sparkles, Tag, History, Undo2,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';

function TaxonomyTree({ taxonomy, onRename, onMerge }) {
  const [expanded, setExpanded] = useState({});
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [mergeSelection, setMergeSelection] = useState({ active: false, level: null, parent: null, items: [] });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const { symbol: cs } = useCurrency();

  const toggle = (path) => setExpanded(p => ({ ...p, [path]: !p[path] }));

  const startRename = (name, level, parent) => {
    setRenaming({ name, level, parent });
    setRenameValue(name);
  };

  const submitRename = async () => {
    if (!renameValue.trim() || renameValue === renaming.name) { setRenaming(null); return; }
    try {
      const res = await api.post('/categories/preview-rename', { level: renaming.level, old_name: renaming.name, new_name: renameValue.trim(), parent: renaming.parent });
      setConfirmDialog({
        type: 'rename',
        message: `Rename "${renaming.name}" to "${renameValue.trim()}"?`,
        detail: `This will update ${res.data.affected_count} transaction(s). A backup will be created so you can undo this later.`,
        onConfirm: () => { onRename(renaming.level, renaming.name, renameValue.trim(), renaming.parent); setConfirmDialog(null); setRenaming(null); },
      });
    } catch {
      setConfirmDialog({
        type: 'rename',
        message: `Rename "${renaming.name}" to "${renameValue.trim()}"?`,
        detail: 'This change cannot be easily undone. A backup will be created.',
        onConfirm: () => { onRename(renaming.level, renaming.name, renameValue.trim(), renaming.parent); setConfirmDialog(null); setRenaming(null); },
      });
    }
  };

  const toggleMergeItem = (name) => {
    setMergeSelection(p => ({
      ...p,
      items: p.items.includes(name) ? p.items.filter(i => i !== name) : [...p.items, name],
    }));
  };

  const startMerge = (level, parent) => {
    setMergeSelection({ active: true, level, parent, items: [] });
  };

  const submitMerge = async () => {
    if (mergeSelection.items.length < 2) { toast.error('Select at least 2 categories to merge'); return; }
    const target = mergeSelection.items[0];
    try {
      const res = await api.post('/categories/preview-merge', { level: mergeSelection.level, source_names: mergeSelection.items, target_name: target, parent: mergeSelection.parent });
      setConfirmDialog({
        type: 'merge',
        message: `Merge ${mergeSelection.items.length} categories into "${target}"?`,
        detail: `This will update ${res.data.affected_count} transaction(s). Categories being merged: ${mergeSelection.items.join(', ')}. A backup will be created so you can undo this later.`,
        onConfirm: () => { onMerge(mergeSelection.level, mergeSelection.items, target, mergeSelection.parent); setConfirmDialog(null); setMergeSelection({ active: false, level: null, parent: null, items: [] }); },
      });
    } catch {
      const target2 = mergeSelection.items[0];
      setConfirmDialog({
        type: 'merge',
        message: `Merge ${mergeSelection.items.length} categories into "${target2}"?`,
        detail: 'This change affects multiple categories. A backup will be created.',
        onConfirm: () => { onMerge(mergeSelection.level, mergeSelection.items, target2, mergeSelection.parent); setConfirmDialog(null); setMergeSelection({ active: false, level: null, parent: null, items: [] }); },
      });
    }
  };

  const renderNode = (node, level, parentPath, parentName) => {
    const path = `${parentPath}/${node.name}`;
    const isExpanded = expanded[path];
    const hasChildren = node.children && node.children.length > 0;
    const levelKey = `level_${level}`;
    const isRenaming = renaming?.name === node.name && renaming?.level === levelKey;
    const isMergeMode = mergeSelection.active && mergeSelection.level === levelKey;
    const isMergeSelected = isMergeMode && mergeSelection.items.includes(node.name);

    return (
      <div key={path} style={{ marginLeft: `${(level - 1) * 16}px` }}>
        <div className={`flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors text-sm group ${isMergeSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'}`}>
          {hasChildren ? (
            <button onClick={() => toggle(path)} className="shrink-0">
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : <div className="w-3.5" />}

          {isMergeMode && (
            <button onClick={() => toggleMergeItem(node.name)} className="shrink-0">
              {isMergeSelected ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
          )}

          {isRenaming ? (
            <div className="flex items-center gap-1 flex-1">
              <Input
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                className="h-6 text-xs px-2"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(null); }}
                data-testid="rename-input"
              />
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={submitRename}><Check className="w-3 h-3 text-green-600" /></Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setRenaming(null)}><X className="w-3 h-3 text-red-500" /></Button>
            </div>
          ) : (
            <>
              <span className="font-medium text-sm flex-1">{node.name}</span>
              <span className="text-xs text-muted-foreground">{node.count} txns</span>
              <span className="text-xs text-muted-foreground">{cs}{(node.spend / 1000).toFixed(0)}k</span>
              {!mergeSelection.active && (
                <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => startRename(node.name, levelKey, parentName)} title="Rename" data-testid={`rename-${node.name}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  {hasChildren && (
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => startMerge(`level_${level + 1}`, node.name)} title="Merge subcategories" data-testid={`merge-children-${node.name}`}>
                      <Merge className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {isExpanded && hasChildren && node.children.map(child => renderNode(child, level + 1, path, node.name))}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="taxonomy-tree">
      {mergeSelection.active && (
        <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 text-xs">
          <Merge className="w-4 h-4 text-amber-600" />
          <span>Select categories to merge ({mergeSelection.items.length} selected)</span>
          <Button size="sm" className="h-6 text-xs ml-auto" onClick={submitMerge} disabled={mergeSelection.items.length < 2} data-testid="confirm-merge-btn">
            Merge Selected
          </Button>
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setMergeSelection({ active: false, level: null, parent: null, items: [] })}>Cancel</Button>
        </div>
      )}
      {!mergeSelection.active && taxonomy.length > 1 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => startMerge('level_1', null)} data-testid="merge-l1-btn">
            <Merge className="w-3 h-3" /> Merge L1 Categories
          </Button>
        </div>
      )}
      {taxonomy.map(node => renderNode(node, 1, '', null))}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-dialog-overlay">
          <div className="bg-background border rounded-lg shadow-lg p-5 max-w-md w-full space-y-3" data-testid="confirm-dialog">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-sm">{confirmDialog.message}</h3>
            </div>
            <p className="text-xs text-muted-foreground">{confirmDialog.detail}</p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => { setConfirmDialog(null); setRenaming(null); }} data-testid="confirm-cancel-btn">Cancel</Button>
              <Button size="sm" variant="destructive" onClick={confirmDialog.onConfirm} data-testid="confirm-proceed-btn">Proceed</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AutoClassifyPanel() {
  const [loading, setLoading] = useState(false);
  const [classifications, setClassifications] = useState([]);
  const [selected, setSelected] = useState({});
  const [edits, setEdits] = useState({});
  const [applying, setApplying] = useState(false);
  const [uncatCount, setUncatCount] = useState(null);
  const { symbol: cs } = useCurrency();

  const checkUncategorized = useCallback(async () => {
    try {
      const res = await api.get('/categories/uncategorized?limit=1');
      setUncatCount(res.data.total);
    } catch {}
  }, []);

  useEffect(() => { checkUncategorized(); }, [checkUncategorized]);

  const runClassification = async () => {
    setLoading(true);
    try {
      const [gptRes, mlRes] = await Promise.all([
        api.post('/categories/auto-classify', { limit: 50 }),
        api.post('/ml/predict-categories').catch(() => ({ data: { predictions: [] } })),
      ]);
      const gptClassifications = gptRes.data.classifications || [];
      const mlPredictions = mlRes.data.predictions || [];

      // Merge ML predictions into GPT classifications
      const mlMap = {};
      mlPredictions.forEach(p => { if (p.ml_available) mlMap[p.id] = p; });

      const merged = gptClassifications.map(c => ({
        ...c,
        ml_category: mlMap[c.id]?.ml_category || null,
        ml_confidence: mlMap[c.id]?.ml_confidence || 0,
      }));

      setClassifications(merged);
      const allSelected = {};
      merged.forEach(c => { allSelected[c.id] = true; });
      setSelected(allSelected);
      if (merged.length === 0) {
        toast.info('No uncategorized transactions found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Classification failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => setSelected(p => ({ ...p, [id]: !p[id] }));

  const toggleAll = () => {
    const allChecked = classifications.every(c => selected[c.id]);
    const next = {};
    classifications.forEach(c => { next[c.id] = !allChecked; });
    setSelected(next);
  };

  const applySelected = async () => {
    const toApply = classifications.filter(c => selected[c.id]).map(c => ({
      id: c.id,
      category_level_1: edits[c.id]?.l1 || c.category_level_1,
      category_level_2: edits[c.id]?.l2 || c.category_level_2,
    }));
    if (toApply.length === 0) return toast.error('No items selected');
    setApplying(true);
    try {
      const res = await api.post('/categories/apply-classifications', { classifications: toApply });
      toast.success(`Applied ${res.data.applied} classifications — can be rolled back from Change History. ML model will retrain in about a minute.`);
      setClassifications(prev => prev.filter(c => !selected[c.id]));
      setSelected({});
      checkUncategorized();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const confColors = { high: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400', medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400', low: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' };

  return (
    <Card data-testid="auto-classify-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2"><Wand2 className="w-4 h-4 text-primary" /> AI Auto-Classification</CardTitle>
            <CardDescription className="text-xs mt-1">Automatically suggest categories for uncategorized transactions using AI</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {uncatCount !== null && (
              <Badge variant={uncatCount > 0 ? 'destructive' : 'secondary'} className="text-xs" data-testid="uncat-count">
                {uncatCount} uncategorized
              </Badge>
            )}
            <Button size="sm" onClick={runClassification} disabled={loading || uncatCount === 0} data-testid="run-classify-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              {loading ? 'Classifying...' : 'Run Classification'}
            </Button>
          </div>
        </div>
      </CardHeader>
      {classifications.length > 0 && (
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <button onClick={toggleAll} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="toggle-all-classify">
              {classifications.every(c => selected[c.id]) ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              Select All ({Object.values(selected).filter(Boolean).length}/{classifications.length})
            </button>
            <Button size="sm" onClick={applySelected} disabled={applying || Object.values(selected).filter(Boolean).length === 0} data-testid="apply-classify-btn">
              {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              Apply Selected
            </Button>
          </div>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {classifications.map(c => (
              <div key={c.id} className={`flex items-center gap-3 p-2 rounded-md border text-xs transition-colors ${selected[c.id] ? 'bg-primary/5 border-primary/30' : 'border-border'}`} data-testid={`classify-row-${c.id}`}>
                <button onClick={() => toggleSelect(c.id)} className="shrink-0">
                  {selected[c.id] ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{c.supplier_name}</span>
                    <span className="text-muted-foreground">{cs}{(c.spend_amount || 0).toLocaleString()}</span>
                  </div>
                  {c.description && <p className="text-muted-foreground truncate mt-0.5">{c.description}</p>}
                </div>
                <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                <div className="shrink-0 space-y-1" data-testid={`classify-edit-${c.id}`}>
                  <Input
                    value={edits[c.id]?.l1 ?? c.category_level_1}
                    onChange={e => setEdits(p => ({ ...p, [c.id]: { ...p[c.id], l1: e.target.value } }))}
                    className="h-5 text-[11px] px-1.5 w-[150px] font-medium"
                    data-testid={`edit-l1-${c.id}`}
                  />
                  <Input
                    value={edits[c.id]?.l2 ?? c.category_level_2 ?? ''}
                    onChange={e => setEdits(p => ({ ...p, [c.id]: { ...p[c.id], l2: e.target.value } }))}
                    className="h-5 text-[11px] px-1.5 w-[150px] text-muted-foreground"
                    placeholder="L2 subcategory"
                    data-testid={`edit-l2-${c.id}`}
                  />
                  {c.ml_category && (
                    <div className="flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-400">
                      <span className="font-medium">ML:</span> {c.ml_category} ({(c.ml_confidence * 100).toFixed(0)}%)
                    </div>
                  )}
                </div>
                <Badge className={`text-[10px] shrink-0 ${confColors[c.confidence] || confColors.medium}`}>{c.confidence}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function BulkReclassifyPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [newCatL1, setNewCatL1] = useState('');
  const [newCatL2, setNewCatL2] = useState('');
  const { symbol: cs } = useCurrency();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/categories/taxonomy');
        setCategories((res.data.taxonomy || []).map(c => c.name));
      } catch {}
    })();
  }, []);

  const searchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat && filterCat !== 'all') params.append('category', filterCat);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '100');
      const res = await api.get(`/categories/search-transactions?${params}`);
      setTransactions(res.data.transactions || []);
      setSelected({});
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => setSelected(p => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    const allChecked = transactions.every(t => selected[t.id]);
    const next = {};
    transactions.forEach(t => { next[t.id] = !allChecked; });
    setSelected(next);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setFilterCat('all');
    setTransactions([]);
    setSelected({});
    setNewCatL1('');
    setNewCatL2('');
  };

  const applyReclassify = async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) return toast.error('No transactions selected');
    if (!newCatL1) return toast.error('Select a target category');
    setApplying(true);
    try {
      const body = { transaction_ids: ids, new_category_level_1: newCatL1 };
      if (newCatL2) body.new_category_level_2 = newCatL2;
      const res = await api.post('/categories/reclassify', body);
      toast.success(`Reclassified ${res.data.modified} transaction(s) — view in Change History to rollback. ML model will retrain in about a minute.`);
      searchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reclassification failed');
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Card data-testid="bulk-reclassify-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4 text-primary" /> Bulk Reclassification</CardTitle>
        <CardDescription className="text-xs">Search transactions and reassign their categories in bulk</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Search bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Input
              placeholder="Search by supplier or description..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="h-8 text-xs pr-7"
              onKeyDown={e => { if (e.key === 'Enter') searchTransactions(); }}
              data-testid="reclassify-search"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search text"
                data-testid="reclassify-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="reclassify-filter-cat">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8" onClick={searchTransactions} disabled={loading} data-testid="reclassify-search-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
          {(searchQuery || filterCat !== 'all' || transactions.length > 0) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={clearSearch}
              data-testid="reclassify-clear-btn"
              title="Clear search, filter, and results"
            >
              Clear
            </Button>
          )}
        </div>

        {/* Results */}
        {transactions.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <button onClick={toggleAll} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="toggle-all-reclass">
                {transactions.every(t => selected[t.id]) ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                {selectedCount}/{transactions.length} selected
              </button>
            </div>
            <div className="space-y-1 max-h-[250px] overflow-y-auto">
              {transactions.map(t => (
                <div key={t.id} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs border transition-colors ${selected[t.id] ? 'bg-primary/5 border-primary/30' : 'border-transparent hover:bg-muted/30'}`} data-testid={`reclass-row-${t.id}`}>
                  <button onClick={() => toggleSelect(t.id)} className="shrink-0">
                    {selected[t.id] ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5 text-muted-foreground" />}
                  </button>
                  <span className="font-medium w-[160px] truncate">{t.supplier_name}</span>
                  <span className="text-muted-foreground w-[100px] truncate">{t.description || '-'}</span>
                  <span className="text-muted-foreground w-[80px]">{cs}{(t.spend_amount || 0).toLocaleString()}</span>
                  <Badge variant="outline" className="text-[10px]">{t.category_level_1 || 'N/A'}</Badge>
                  {t.category_level_2 && <Badge variant="outline" className="text-[10px]">{t.category_level_2}</Badge>}
                </div>
              ))}
            </div>

            {/* Target category */}
            {selectedCount > 0 && (
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg border border-border" data-testid="reclassify-target">
                <span className="text-xs font-medium shrink-0">Move {selectedCount} txn(s) to:</span>
                <Select value={newCatL1} onValueChange={setNewCatL1}>
                  <SelectTrigger className="w-[180px] h-7 text-xs" data-testid="target-cat-l1">
                    <SelectValue placeholder="Select L1 Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="L2 subcategory (optional)"
                  value={newCatL2}
                  onChange={e => setNewCatL2(e.target.value)}
                  className="h-7 text-xs w-[160px]"
                  data-testid="target-cat-l2"
                />
                <Button size="sm" className="h-7 text-xs" onClick={applyReclassify} disabled={applying || !newCatL1} data-testid="apply-reclassify-btn">
                  {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                  Apply
                </Button>
              </div>
            )}
          </>
        )}

        {!loading && transactions.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">Search for transactions to reclassify</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function CategoryManagement() {
  const [taxonomy, setTaxonomy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changeHistory, setChangeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const fetchTaxonomy = async () => {
    try {
      const res = await api.get('/categories/taxonomy');
      setTaxonomy(res.data.taxonomy || []);
    } catch {
      toast.error('Failed to load taxonomy');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/categories/change-history');
      setChangeHistory(res.data.changes || []);
    } catch {}
  };

  useEffect(() => { fetchTaxonomy(); fetchHistory(); }, []);

  const handleRename = async (level, oldName, newName, parent) => {
    try {
      const res = await api.post('/categories/rename', { level, old_name: oldName, new_name: newName, parent });
      toast.success(`Renamed "${oldName}" to "${newName}" (${res.data.modified} txns updated). ML model will retrain in about a minute.`);
      fetchTaxonomy();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const handleMerge = async (level, sourceNames, targetName, parent) => {
    try {
      const res = await api.post('/categories/merge', { level, source_names: sourceNames, target_name: targetName, parent });
      toast.success(`Merged ${sourceNames.length} categories into "${targetName}" (${res.data.modified} txns updated). ML model will retrain in about a minute.`);
      fetchTaxonomy();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    }
  };

  const handleRollback = async (changeId) => {
    setRollingBack(changeId);
    try {
      const res = await api.post(`/categories/rollback/${changeId}`);
      toast.success(`Rolled back: ${res.data.restored} transactions restored. ML model will retrain in about a minute.`);
      fetchTaxonomy();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setRollingBack(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="category-management-tab">
      {/* AI Auto-Classification */}
      <AutoClassifyPanel />

      {/* Taxonomy Tree */}
      <Card data-testid="taxonomy-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2"><FolderTree className="w-4 h-4 text-primary" /> Category Taxonomy</CardTitle>
              <CardDescription className="text-xs mt-1">Rename, merge, or reorganize your procurement categories</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={fetchTaxonomy} data-testid="refresh-taxonomy-btn">
              <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <TaxonomyTree taxonomy={taxonomy} onRename={handleRename} onMerge={handleMerge} />
        </CardContent>
      </Card>

      {/* Bulk Reclassification */}
      <BulkReclassifyPanel />

      {/* Change History */}
      <Card data-testid="change-history-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Change History</CardTitle>
              <CardDescription className="text-xs mt-1">Recent category changes with rollback capability</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">{changeHistory.length} changes</Badge>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }} data-testid="toggle-history-btn">
                {showHistory ? 'Hide' : 'Show'}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showHistory && (
          <CardContent>
            {changeHistory.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {changeHistory.map(ch => (
                  <div key={ch.change_id} className={`flex items-center gap-3 p-2.5 rounded-md border text-xs ${ch.rolled_back ? 'opacity-50 border-dashed' : 'border-border'}`} data-testid={`history-row-${ch.change_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant={ch.action === 'merge' ? 'default' : 'secondary'} className="text-[10px]">{ch.action === 'ai_classify' ? 'AI classify' : ch.action}</Badge>
                        <span className="font-medium">{ch.details}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-muted-foreground">
                        <span>{ch.affected_count} txns</span>
                        <span>by {ch.performed_by}</span>
                        <span>{new Date(ch.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    {ch.rolled_back ? (
                      <Badge variant="outline" className="text-[10px] shrink-0">Rolled back</Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-xs gap-1 shrink-0"
                        onClick={() => handleRollback(ch.change_id)}
                        disabled={rollingBack === ch.change_id}
                        data-testid={`rollback-${ch.change_id}`}
                      >
                        {rollingBack === ch.change_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                        Undo
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">No changes recorded yet</p>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
